export const RAID_CONTROL_REPRACTICE_REQUEST_REF = '__doroparty_repractice_request_v1__';
export const RAID_CONTROL_REPRACTICE_REQUEST_VERSION = 1;

export const RAID_CONTROL_REPRACTICE_REQUEST_STATUS = Object.freeze({
  PENDING: 'pending',
  COMPLETED: 'completed',
  BELOW_CUTOFF: 'below_cutoff',
  EXPIRED: 'expired',
});

const HARD_ORDER_STATUSES = new Set(['sent', 'acknowledged', 'attacking', 'held', 'pending_approval']);
const text = (value) => String(value ?? '').trim();

function number(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function roundEok(value) {
  return Math.round(number(value) * 1000) / 1000;
}

function memberKey(value = {}) {
  return text(
    value.memberOpenid
    ?? value.member_openid
    ?? value.targetMemberOpenid
    ?? value.target_member_openid
    ?? value.openid
    ?? value.memberId
    ?? value.member_id,
  );
}

function practiceKey(value = {}) {
  return text(value.id ?? value.practiceId ?? value.practice_id);
}

function normalizeDeck(value) {
  return (Array.isArray(value) ? value : [])
    .map((item) => text(item?.nikkeId ?? item?.nikke_id ?? item?.code ?? item?.id ?? item))
    .filter(Boolean)
    .slice(0, 5);
}

function sameDeck(left, right) {
  const a = normalizeDeck(left);
  const b = normalizeDeck(right);
  return a.length === 5 && b.length === 5 && a.every((code, index) => code === b[index]);
}

function practiceTimestamp(row = {}) {
  const raw = row.updatedAt ?? row.updated_at ?? row.createdAt ?? row.created_at ?? '';
  const parsed = Date.parse(String(raw || ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stableValue(value[key]);
    return result;
  }, {});
}

function stableSerialize(value) {
  return JSON.stringify(stableValue(value));
}

function hashRevision(prefix, value) {
  const source = stableSerialize(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${prefix}-${hash.toString(16).padStart(8, '0')}`;
}

function normalizedActualRecord(row = {}) {
  return {
    id: text(row.id ?? row.recordKey ?? row.record_key),
    bossNo: Math.trunc(number(row.bossNo ?? row.boss_no)),
    phaseNo: Math.trunc(number(row.phaseNo ?? row.phase_no)),
    attackNo: Math.trunc(number(row.attackNo ?? row.attack_no)),
    damageEok: roundEok(row.damageEok ?? row.damage_eok),
    deck: normalizeDeck(row.deck ?? row.deckCodes ?? row.deck_codes),
  };
}

function normalizedOrder(row = {}) {
  return {
    id: text(row.id),
    status: text(row.status ?? row.orderStatus ?? row.order_status ?? row.state).toLocaleLowerCase('en-US'),
    bossNo: Math.trunc(number(row.bossNo ?? row.boss_no)),
    phaseNo: Math.trunc(number(row.phaseNo ?? row.phase_no)),
    deck: normalizeDeck(row.deck ?? row.deckCodes ?? row.deck_codes),
  };
}

function normalizedPractice(row = {}) {
  return {
    id: practiceKey(row),
    memberOpenid: memberKey(row),
    bossNo: Math.trunc(number(row.bossNo ?? row.boss_no)),
    slotNo: Math.trunc(number(row.slotNo ?? row.slot_no)),
    damageEok: roundEok(row.damageEok ?? row.damage_eok),
    deck: normalizeDeck(row.deck ?? row.deckCodes ?? row.deck_codes),
    updatedAt: text(row.updatedAt ?? row.updated_at ?? row.createdAt ?? row.created_at),
  };
}

function normalizedBoss(boss = {}) {
  return {
    bossNo: Math.trunc(number(boss.bossNo ?? boss.boss_no)),
    phaseNo: Math.trunc(number(boss.currentPhase ?? boss.phaseNo ?? boss.phase_no, 1)),
    remainingHpEok: roundEok(boss.remainingHpEok ?? boss.remaining_hp_eok),
  };
}

export function createRaidControlRepracticeStateRevision({
  seasonNo = 0,
  boss = {},
  memberOpenid = '',
  basePracticeId = '',
  practiceRecords = [],
  actualRecords = [],
  lockedOrders = [],
} = {}) {
  const targetMember = text(memberOpenid);
  const targetBoss = normalizedBoss(boss);
  const practices = (Array.isArray(practiceRecords) ? practiceRecords : [])
    .filter((row) => memberKey(row) === targetMember && Math.trunc(number(row.bossNo ?? row.boss_no)) === targetBoss.bossNo)
    .map(normalizedPractice)
    .sort((left, right) => left.slotNo - right.slotNo || left.id.localeCompare(right.id));
  const actual = (Array.isArray(actualRecords) ? actualRecords : [])
    .filter((row) => memberKey(row) === targetMember)
    .map(normalizedActualRecord)
    .sort((left, right) => left.attackNo - right.attackNo || left.bossNo - right.bossNo || left.id.localeCompare(right.id));
  const orders = (Array.isArray(lockedOrders) ? lockedOrders : [])
    .filter((row) => memberKey(row) === targetMember)
    .map(normalizedOrder)
    .filter((row) => HARD_ORDER_STATUSES.has(row.status))
    .sort((left, right) => left.id.localeCompare(right.id) || left.status.localeCompare(right.status));

  return hashRevision('raid-state-v1', {
    seasonNo: Math.trunc(number(seasonNo)),
    boss: targetBoss,
    memberOpenid: targetMember,
    basePracticeId: text(basePracticeId),
    practices,
    actual,
    orders,
  });
}

export function normalizeRaidControlRepracticeRequest(raw = null) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const bossNo = Math.trunc(number(raw.bossNo ?? raw.boss_no));
  const seasonNo = Math.trunc(number(raw.seasonNo ?? raw.season_no));
  const requiredMeasuredDamageEok = roundEok(raw.requiredMeasuredDamageEok ?? raw.required_measured_damage_eok);
  const memberOpenid = text(raw.memberOpenid ?? raw.member_openid);
  const candidateId = text(raw.candidateId ?? raw.candidate_id);
  const variantDeck = normalizeDeck(raw.variantDeck ?? raw.variant_deck);
  if (!candidateId || !memberOpenid || seasonNo <= 0 || bossNo < 1 || bossNo > 5 || requiredMeasuredDamageEok <= 0) return null;

  return {
    kind: 'raid-control-repractice-request',
    version: RAID_CONTROL_REPRACTICE_REQUEST_VERSION,
    candidateId,
    memberOpenid,
    memberNickname: text(raw.memberNickname ?? raw.member_nickname) || '멤버',
    targetUserId: text(raw.targetUserId ?? raw.target_user_id),
    seasonNo,
    bossNo,
    bossName: text(raw.bossName ?? raw.boss_name) || `${bossNo}번 보스`,
    phaseNo: Math.max(1, Math.trunc(number(raw.phaseNo ?? raw.phase_no, 1))),
    basePracticeId: text(raw.basePracticeId ?? raw.base_practice_id),
    basePracticeDamageEok: roundEok(raw.basePracticeDamageEok ?? raw.base_practice_damage_eok),
    requiredMeasuredDamageEok,
    removeNikkeId: text(raw.removeNikkeId ?? raw.remove_nikke_id),
    removeNikkeName: text(raw.removeNikkeName ?? raw.remove_nikke_name),
    addNikkeId: text(raw.addNikkeId ?? raw.add_nikke_id),
    addNikkeName: text(raw.addNikkeName ?? raw.add_nikke_name),
    variantDeck,
    availability: text(raw.availability) || 'unknown',
    requestRole: text(raw.requestRole ?? raw.request_role) || 'confirm_availability',
    targetFit: text(raw.targetFit ?? raw.target_fit) || 'unknown',
    confidence: text(raw.confidence) || 'low',
    reasonCodes: (Array.isArray(raw.reasonCodes ?? raw.reason_codes) ? raw.reasonCodes ?? raw.reason_codes : [])
      .map(text)
      .filter(Boolean)
      .slice(0, 8),
    requiresPracticeConfirmation: true,
    stateRevision: text(raw.stateRevision ?? raw.state_revision),
    planRevision: text(raw.planRevision ?? raw.plan_revision),
  };
}

export function createRaidControlRepracticeRequestRef({
  request = null,
  seasonNo = 0,
  boss = {},
  targetUserId = '',
  practiceRecords = [],
  actualRecords = [],
  lockedOrders = [],
} = {}) {
  if (!request || typeof request !== 'object') return null;
  const stateRevision = createRaidControlRepracticeStateRevision({
    seasonNo,
    boss,
    memberOpenid: request.memberOpenid,
    basePracticeId: request.basePracticeId,
    practiceRecords,
    actualRecords,
    lockedOrders,
  });
  const base = normalizeRaidControlRepracticeRequest({
    ...request,
    seasonNo,
    bossNo: boss?.bossNo ?? request.bossNo,
    bossName: boss?.bossName ?? request.bossName,
    phaseNo: boss?.currentPhase ?? request.phaseNo,
    targetUserId,
    stateRevision,
    planRevision: 'pending',
  });
  if (!base) return null;
  const planRevision = hashRevision('repractice-plan-v1', {
    stateRevision,
    candidateId: base.candidateId,
    basePracticeId: base.basePracticeId,
    requiredMeasuredDamageEok: base.requiredMeasuredDamageEok,
    removeNikkeId: base.removeNikkeId,
    addNikkeId: base.addNikkeId,
    variantDeck: base.variantDeck,
    availability: base.availability,
  });
  const payload = Object.freeze({ ...base, stateRevision, planRevision });
  return {
    nikkeId: `${RAID_CONTROL_REPRACTICE_REQUEST_REF}:${encodeURIComponent(JSON.stringify(payload))}`,
    name: '모의전 요청',
    imageUrl: '',
    repracticeRequest: payload,
  };
}

export function isRaidControlRepracticeRequestRef(raw = null) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const id = text(raw.nikkeId ?? raw.nikke_id ?? raw.code ?? raw.id);
  return id === RAID_CONTROL_REPRACTICE_REQUEST_REF
    || id.startsWith(`${RAID_CONTROL_REPRACTICE_REQUEST_REF}:`)
    || Boolean(raw.repracticeRequest ?? raw.repractice_request);
}

export function extractRaidControlRepracticeRequest(rawRefs = []) {
  const source = (Array.isArray(rawRefs) ? rawRefs : []).find(isRaidControlRepracticeRequestRef);
  if (!source) return null;
  const nested = source.repracticeRequest ?? source.repractice_request;
  if (nested) return normalizeRaidControlRepracticeRequest(nested);
  const id = text(source.nikkeId ?? source.nikke_id ?? source.code ?? source.id);
  const encoded = id.startsWith(`${RAID_CONTROL_REPRACTICE_REQUEST_REF}:`)
    ? id.slice(RAID_CONTROL_REPRACTICE_REQUEST_REF.length + 1)
    : '';
  if (!encoded) return null;
  try {
    return normalizeRaidControlRepracticeRequest(JSON.parse(decodeURIComponent(encoded)));
  } catch {
    return null;
  }
}

export function raidControlRepracticeRequestBody(rawRequest) {
  const request = normalizeRaidControlRepracticeRequest(rawRequest);
  if (!request) return '';
  const swap = request.removeNikkeName || request.addNikkeName
    ? `${request.removeNikkeName || '기존'} → ${request.addNikkeName || '변경'}`
    : '덱 변경';
  const current = request.basePracticeDamageEok > 0 ? ` · 현재 ${request.basePracticeDamageEok.toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억` : '';
  return `@${request.memberNickname} 모의전 요청 · ${request.bossName} ${request.phaseNo}단 · ${swap}${current} · 필요 실측 ${request.requiredMeasuredDamageEok.toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억 이상`;
}

export function isRaidControlRepracticeRequestStale(rawRequest, {
  seasonNo = 0,
  boss = {},
  practiceRecords = [],
  actualRecords = [],
  lockedOrders = [],
} = {}) {
  const request = normalizeRaidControlRepracticeRequest(rawRequest);
  if (!request || !request.stateRevision) return true;
  if (request.seasonNo !== Math.trunc(number(seasonNo))) return true;
  if (request.bossNo !== Math.trunc(number(boss?.bossNo ?? boss?.boss_no))) return true;
  const currentRevision = createRaidControlRepracticeStateRevision({
    seasonNo,
    boss,
    memberOpenid: request.memberOpenid,
    basePracticeId: request.basePracticeId,
    practiceRecords,
    actualRecords,
    lockedOrders,
  });
  return currentRevision !== request.stateRevision;
}

export function resolveRaidControlRepracticeRequestStatus(rawRequest, {
  messageCreatedAt = '',
  seasonNo = 0,
  boss = {},
  practiceRecords = [],
  actualRecords = [],
  lockedOrders = [],
} = {}) {
  const request = normalizeRaidControlRepracticeRequest(rawRequest);
  if (!request) return Object.freeze({
    status: RAID_CONTROL_REPRACTICE_REQUEST_STATUS.EXPIRED,
    measuredDamageEok: null,
    matchedPracticeId: '',
    cutoffPassed: false,
  });

  const requestedAt = Date.parse(String(messageCreatedAt || ''));
  const candidates = (Array.isArray(practiceRecords) ? practiceRecords : [])
    .filter((row) => memberKey(row) === request.memberOpenid)
    .filter((row) => Math.trunc(number(row.bossNo ?? row.boss_no)) === request.bossNo)
    .filter((row) => sameDeck(row.deck ?? row.deckCodes ?? row.deck_codes, request.variantDeck))
    .filter((row) => {
      const changedAt = practiceTimestamp(row);
      if (Number.isFinite(requestedAt) && requestedAt > 0 && changedAt > 0) return changedAt >= requestedAt;
      if (practiceKey(row) === request.basePracticeId) {
        return roundEok(row.damageEok ?? row.damage_eok) !== request.basePracticeDamageEok;
      }
      return changedAt > 0 || !Number.isFinite(requestedAt) || requestedAt <= 0;
    })
    .sort((left, right) => practiceTimestamp(right) - practiceTimestamp(left)
      || Math.trunc(number(right.slotNo ?? right.slot_no)) - Math.trunc(number(left.slotNo ?? left.slot_no))
      || practiceKey(right).localeCompare(practiceKey(left)));

  const result = candidates[0] || null;
  if (result) {
    const measuredDamageEok = roundEok(result.damageEok ?? result.damage_eok);
    const cutoffPassed = measuredDamageEok >= request.requiredMeasuredDamageEok;
    return Object.freeze({
      status: cutoffPassed
        ? RAID_CONTROL_REPRACTICE_REQUEST_STATUS.COMPLETED
        : RAID_CONTROL_REPRACTICE_REQUEST_STATUS.BELOW_CUTOFF,
      measuredDamageEok,
      matchedPracticeId: practiceKey(result),
      cutoffPassed,
      measuredAt: text(result.updatedAt ?? result.updated_at ?? result.createdAt ?? result.created_at),
    });
  }

  const stale = isRaidControlRepracticeRequestStale(request, {
    seasonNo,
    boss,
    practiceRecords,
    actualRecords,
    lockedOrders,
  });
  return Object.freeze({
    status: stale ? RAID_CONTROL_REPRACTICE_REQUEST_STATUS.EXPIRED : RAID_CONTROL_REPRACTICE_REQUEST_STATUS.PENDING,
    measuredDamageEok: null,
    matchedPracticeId: '',
    cutoffPassed: false,
  });
}
